import { useState, useEffect, useMemo, useCallback } from 'react';

import {
	useGetOnboardingState,
	useSetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_INSIGHTS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useShouldExcludeUser } from '@confluence/targeting';
import { useExperimentalSiteUserPersonalization } from '@confluence/targeting/entry-points/useExperimentalSiteUserPersonalization.ts';
import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

const ONBOARDING_DELAY_MS = 3000;
const MIN_READ_TIME_MINUTES = 5;
const CONFLUENCE_FIRST_ACTIVE_TRAIT = 'confluence_first_active';

export const useAudioV3Changeboarding = ({
	contentId,
	onShow,
	skip,
	readTime = 5,
}: {
	contentId: string;
	onShow: () => void;
	skip: boolean;
	readTime?: number | null;
}) => {
	const [showChangeboarding, setShowChangeboarding] = useState(false);

	const validReadTime = readTime && readTime >= MIN_READ_TIME_MINUTES;

	const { cloudId, userId } = useSessionData();
	const isLicensed = useIsLicensed();

	const { data: userPersonalizationData, loading: userTargetingLoading } =
		useExperimentalSiteUserPersonalization({
			variables: { cloudId, userId: userId as string },
			skip: !isLicensed || !userId,
		});

	const attributes = userPersonalizationData?.experimentalSiteUserPersonalization?.attributes;
	const confluenceFirstActive = attributes?.find(
		({ name }: { name: string }) => name === CONFLUENCE_FIRST_ACTIVE_TRAIT,
	)?.value;

	const firstActiveDate = confluenceFirstActive ? new Date(confluenceFirstActive) : null;

	const { shouldExclude: shouldExcludeNewUser } = useShouldExcludeUser({
		checkDate: firstActiveDate,
		daysThreshold: 28,
	});

	const shouldSkip =
		skip ||
		!validReadTime ||
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldExcludeNewUser ||
		userTargetingLoading;

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([ONBOARDING_INSIGHTS.AUDIO_V3_CHANGEBOARDING], shouldSkip);

	const { setOnboardingState } = useSetOnboardingState();
	const { hasAudioV3ChangeboardingBeenSeen } = deserializeState(onboardingStateData);

	const changeboardingEligible = useMemo(() => {
		const areConditionsMet =
			!shouldSkip &&
			!onboardingStateLoading &&
			!onboardingStateError &&
			!hasAudioV3ChangeboardingBeenSeen;

		return !!areConditionsMet;
	}, [onboardingStateLoading, onboardingStateError, hasAudioV3ChangeboardingBeenSeen, shouldSkip]);

	// The effect that auto opens the popper
	useEffect(() => {
		// Always reset spotlight on content change or if conditions change
		setShowChangeboarding(false);

		if (changeboardingEligible) {
			const timer = setTimeout(() => {
				setShowChangeboarding(true);
				onShow();
			}, ONBOARDING_DELAY_MS);
			return () => {
				clearTimeout(timer);
			};
		}
	}, [contentId, changeboardingEligible, onShow]);

	const handleCloseChangeboarding = useCallback(() => {
		setOnboardingState({
			key: ONBOARDING_INSIGHTS.AUDIO_V3_CHANGEBOARDING,
			value: 'true',
		});
		setShowChangeboarding(false);
	}, [setOnboardingState]);

	return { changeboardingEligible, showChangeboarding, handleCloseChangeboarding };
};
